import { useContext, useEffect, useState } from 'react'
import { Button, InlineAlert } from '../components/BaseComponents'
import CanonicalModal from '../components/BaseComponents/CanonicalModal/CanonicalModal'
import { getApiErrorMessage, getCSRFToken } from '../lib/utils'
import usePublicApi from '../hooks/usePublicApi'
import useApi from '../hooks/useApi'
import { TEXT_LINK_FOCUS_RING } from '../lib/a11yFocus'
import { userContext } from '../context/UserContext'

type NoOrganisationProps = {
	email?: string
	fullName?: string
}

type PendingInvitation = {
	id: string
	companyName: string
	invitedAt: string
}

// Holding screen for a signed-in user with no active organisation membership (shown by
// UserContext before Router mounts, like TrainingNotStarted). Lists in-app invitations (B-6a).
function NoOrganisation({ email, fullName }: NoOrganisationProps) {
	const publicApi = usePublicApi()
	const api = useApi()
	const { clearUserData, fetchUserData } = useContext(userContext)
	const [invitations, setInvitations] = useState<PendingInvitation[]>([])
	const [decliningInvitation, setDecliningInvitation] = useState<PendingInvitation | null>(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState('')

	const loadInvitations = async () => {
		try {
			const body = await api.get('pendingInvitations')
			setInvitations(Array.isArray(body?.data) ? body.data : [])
		} catch (_) {
			setInvitations([])
		}
	}

	useEffect(() => {
		loadInvitations()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const onLogout = async () => {
		await publicApi.post('/user/logout', {
			_csrf: await getCSRFToken(),
		})
		clearUserData()
		window.location.reload()
	}

	const onAccept = async (invitation: PendingInvitation) => {
		setBusy(true)
		setError('')
		try {
			await api.post(`pendingInvitations/${invitation.id}/accept`, { _csrf: await getCSRFToken() })
			// The new membership makes this screen unnecessary once the session is refreshed.
			await fetchUserData()
		} catch (err) {
			setError(getApiErrorMessage(err, 'Failed to accept this invitation. Please try again.'))
		} finally {
			setBusy(false)
		}
	}

	const onDecline = async () => {
		if (!decliningInvitation) {
			return
		}
		setBusy(true)
		setError('')
		try {
			await api.post(`pendingInvitations/${decliningInvitation.id}/decline`, { _csrf: await getCSRFToken() })
			setDecliningInvitation(null)
			await loadInvitations()
		} catch (err) {
			setDecliningInvitation(null)
			setError(getApiErrorMessage(err, 'Failed to decline this invitation. Please try again.'))
		} finally {
			setBusy(false)
		}
	}

	return (
		// Renders outside AppRouter's flex wrapper, so the flex container stops the inner mt-16 collapsing through #root.
		<div className='flex flex-col items-center min-h-dvh'>
			<div className='content w-[32rem] mt-16 text-center'>
				<div className='text-charcoal-100 text-2xl'>{fullName ? `Welcome to Neoni, ${fullName}` : 'Welcome to Neoni'}</div>
				<div className='mt-8 text-charcoal-100'>
					Your account{email && <> for <span>{email}</span></>} has not yet been added to an organisation. An administrator needs to add you before you can continue. If you have just signed up and do not see the organisation you expected, nor a sample organisation, then please contact our Support Team by emailing <a className={`underline text-algae-500 ${TEXT_LINK_FOCUS_RING}`} href='mailto:support@neoni-app.com'>support@neoni-app.com</a>.
				</div>
				{error && (
					<InlineAlert type='error' className='mt-6'>{error}</InlineAlert>
				)}
				{invitations.length > 0 && (
					<div className='mt-8 text-left'>
						<div className='text-charcoal-100'>Your invitations</div>
						<ul className='mt-2 space-y-3'>
							{invitations.map((invitation) => (
								<li key={invitation.id} className='rounded border border-charcoal-300 p-4'>
									<div className='text-charcoal-100'>
										You have been invited to join <b>{invitation.companyName}</b> on Neoni.
									</div>
									<div className='mt-3 flex space-x-3'>
										<Button
											type='Primary'
											text='Accept invitation'
											disabled={busy}
											onClick={() => onAccept(invitation)}
										/>
										<Button
											type='Outlined'
											text='Decline invitation'
											disabled={busy}
											onClick={() => setDecliningInvitation(invitation)}
										/>
									</div>
								</li>
							))}
						</ul>
					</div>
				)}
				{email && (
					<div className='mt-8 text-charcoal-100'>
						Signed in as <span>{email}</span>.
					</div>
				)}
				<Button type='Primary' text='Log out' additionalClass={email ? 'mt-6' : 'mt-8'} onClick={onLogout} />
			</div>
			<CanonicalModal
				open={decliningInvitation !== null}
				onClose={() => setDecliningInvitation(null)}
				size='small'
				title='Decline invitation'
				subtitle={decliningInvitation ? decliningInvitation.companyName : undefined}
				actions={
					<div className='flex space-x-3'>
						<Button type='Primary' text='Confirm decline' disabled={busy} onClick={onDecline} />
						<Button type='Outlined' text='Cancel' disabled={busy} onClick={() => setDecliningInvitation(null)} />
					</div>
				}
			>
				<div className='mt-6 text-charcoal-100'>
					The administrator will see that you declined. They can send you a new invitation later.
				</div>
			</CanonicalModal>
		</div>
	)
}

export default NoOrganisation
